// Hidden per LMP-5588: not mounted — uncomment <ngz-notification-modals> in src/index.html to restore.
import React from 'react';

import { observer } from 'mobx-react';
import { useAsync } from 'react-use';

import { angularizeWithProviders } from '../../../shared/providers';
import { useIsActivePage } from '../../../shared/hooks/useIsActivePage';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { solutionsStore } from '../../../shared/stores/solutionsStore';
import { challengeTutorialStore } from '../../store/challengeTutorialStore';
import { whatsNewStore } from '../../store/whatsNewStore';
import { ChallengeTutorialModal } from './ChallengeTutorialModal/ChallengeTutorialModal';
import { WhatsNewModal } from './WhatsNewModal/WhatsNewModal';

//List of pages where we don't need to show whats new
const bannedPages = [
  // root is a transit hub: all error flows (401/402/...) dump the user to '/' and
  // route guards redirect further; fix redirect error loop LMP-5348
  '/',
  '/terms-and-conditions',
  '/self-billing/setup-card',
  '/self-billing/failed-payment',
  '/challenge-preview/:challengeId',
  '/challenge-landing/:challengeId',
  '/challenge-leaderboard/:challengeId',
  '/challenge-sign-up/:challengeId',
  '/nutrition-account-not-found',
  '/already-participant',
];

const NgzNotificationModals = observer(() => {
  const { loggedInUser, isEntity, isCoach } = loggedInUserDataStore;
  const { solutionTypeFlags, activeSolutionFlags } = solutionsStore;

  const getChallengeTutorialInfo = async (userId: number) => {
    if ((isEntity || isCoach) && solutionTypeFlags.CHALLENGE) {
      await Promise.all([
        challengeTutorialStore.getVideoUrl(),
        challengeTutorialStore.getModalStatus(userId),
      ]);
    }
  };

  const getWhatsNewInfo = async (userId: number) => {
    if (isEntity || isCoach) {
      await whatsNewStore.getModalStatus(userId);
    }
  };

  const getInformationAboutModals = async () => {
    if (loggedInUser?.id) {
      await Promise.all([
        getChallengeTutorialInfo(loggedInUser.id),
        getWhatsNewInfo(loggedInUser.id),
      ]);
    }
  };

  const { loading } = useAsync(getInformationAboutModals, [loggedInUser?.id]);

  const isBannedPage = useIsActivePage({ urls: bannedPages });

  if (isBannedPage || loading) {
    return null;
  }

  const canShowWhatsNew = whatsNewStore.open;
  // On CHALLENGE-scoped pages: show for both auto (EDIT) and manual (VIEW) modes.
  // On general/settings pages (no active solution): only show when explicitly
  const isInChallengeContext =
    activeSolutionFlags.CHALLENGE || challengeTutorialStore.forceView;
  const canShowChallengeTutorial = challengeTutorialStore.open && isInChallengeContext;

  // order is important, represent priority of modals
  if (canShowChallengeTutorial && challengeTutorialStore.mode) {
    return (
      <ChallengeTutorialModal
        mode={challengeTutorialStore.mode}
        videoUrl={challengeTutorialStore.videoUrl}
        onClose={challengeTutorialStore.closeModal}
        showAgain={challengeTutorialStore.showAgain}
        onShowAgainChange={challengeTutorialStore.changeShowAgain}
      />
    );
  }

  if (canShowWhatsNew) {
    return <WhatsNewModal onClose={whatsNewStore.closeModal} />;
  }

  return null;
});

angularizeWithProviders(NgzNotificationModals, 'ngzNotificationModals', {});
